import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../context/AuthContext'
import {
  ChevronLeft, ChevronRight, Circle, Plus, X,
  Clock, Users, Pencil, Trash2, List, CalendarDays,
} from 'lucide-react'

const GIORNI = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom']
const MESI = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre']

const PRIORITA_COLOR = { alta: 'bg-red-400', media: 'bg-yellow-400', bassa: 'bg-sky-400' }

const isoDate = (d) => d.toISOString().split('T')[0]
const startOfMonth = (y, m) => new Date(y, m, 1)
const endOfMonth   = (y, m) => new Date(y, m + 1, 0)
const dowLun = (d) => (d.getDay() + 6) % 7

const fmtTime = (t) => t ? t.slice(0, 5) : ''
const labelData = (dateStr) => {
  const [y, m, d] = dateStr.split('-')
  return `${parseInt(d)} ${MESI[parseInt(m) - 1]} ${y}`
}

/* ─── Avatar mini ─── */
const AvatarMini = ({ profile, size = 6 }) => {
  const letter = (profile?.nickname || profile?.full_name || '?')[0].toUpperCase()
  return profile?.avatar_url
    ? <img src={profile.avatar_url} alt="" className={`w-${size} h-${size} rounded-full object-cover`} />
    : <div className={`w-${size} h-${size} rounded-full bg-violet-600 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0`}>{letter}</div>
}

/* ─── Modal nuovo evento / task ─── */
const NewEventModal = ({ dateStr, progetti, teamMembers, myId, onClose, onSaveEvento, onSaveTask }) => {
  const [tab, setTab]             = useState('evento')
  const [titolo, setTitolo]       = useState('')
  const [oraInizio, setOraInizio] = useState('')
  const [oraFine, setOraFine]     = useState('')
  const [descrizione, setDesc]    = useState('')
  const [projectId, setProjectId] = useState('')
  const [partecipanti, setPartecipanti] = useState([])
  const [tTask, setTTask]         = useState('')
  const [priorita, setPriorita]   = useState('media')
  const [projectIdTask, setProjectIdTask] = useState('')
  const [saving, setSaving]       = useState(false)

  const togglePartecipante = (id) =>
    setPartecipanti(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])

  const handleSave = async () => {
    if (tab === 'evento' && !titolo.trim()) return
    if (tab === 'task'   && !tTask.trim()) return
    setSaving(true)
    if (tab === 'evento') {
      await onSaveEvento({ titolo: titolo.trim(), oraInizio, oraFine, descrizione, projectId, partecipanti })
    } else {
      await onSaveTask({ titolo: tTask.trim(), priorita, projectId: projectIdTask })
    }
    setSaving(false)
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-5 pb-4">
          <div>
            <div className="flex gap-2 mb-0.5">
              {['evento', 'task'].map(t => (
                <button key={t} onClick={() => setTab(t)}
                  className={`text-sm font-semibold capitalize px-3 py-1 rounded-lg transition-colors ${
                    tab === t ? 'bg-violet-600 text-white' : 'text-gray-400 hover:text-white'
                  }`}>{t}</button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">{labelData(dateStr)}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors"><X size={18} /></button>
        </div>

        <div className="px-5 pb-5 space-y-4">
          {tab === 'evento' ? (
            <>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Titolo</label>
                <input autoFocus value={titolo} onChange={e => setTitolo(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSave()}
                  placeholder="Nome dell'evento..."
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-violet-500 transition-colors" />
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1.5">Dalle</label>
                  <input type="time" value={oraInizio} onChange={e => setOraInizio(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500 transition-colors" />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1.5">Alle</label>
                  <input type="time" value={oraFine} onChange={e => setOraFine(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500 transition-colors" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Descrizione (opz.)</label>
                <textarea value={descrizione} onChange={e => setDesc(e.target.value)} rows={2}
                  placeholder="Note sull'evento..."
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-violet-500 resize-none transition-colors" />
              </div>
              {progetti.length > 0 && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Progetto (opz.)</label>
                  <select value={projectId} onChange={e => setProjectId(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-violet-500 transition-colors">
                    <option value="">— nessuno —</option>
                    {progetti.map(p => <option key={p.id} value={p.id}>{p.titolo}</option>)}
                  </select>
                </div>
              )}
              {teamMembers.length > 0 && (
                <div>
                  <label className="block text-xs text-gray-500 mb-2">Partecipanti</label>
                  <div className="flex flex-wrap gap-2">
                    {teamMembers.filter(m => m.id !== myId).map(m => {
                      const sel = partecipanti.includes(m.id)
                      const name = m.nickname || m.full_name || '?'
                      return (
                        <button key={m.id} onClick={() => togglePartecipante(m.id)} title={name}
                          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-medium border transition-all ${
                            sel
                              ? 'bg-violet-600/20 border-violet-500 text-violet-300'
                              : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500'
                          }`}>
                          <AvatarMini profile={m} size={4} />
                          {name}
                        </button>
                      )
                    })}
                  </div>
                  {partecipanti.length > 0 && (
                    <p className="text-xs text-violet-400 mt-1.5">{partecipanti.length} selezionati</p>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Titolo</label>
                <input autoFocus value={tTask} onChange={e => setTTask(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSave()}
                  placeholder="Nome del task..."
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-violet-500 transition-colors" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Priorità</label>
                <div className="flex gap-2">
                  {['alta', 'media', 'bassa'].map(p => (
                    <button key={p} onClick={() => setPriorita(p)}
                      className={`flex-1 py-2 rounded-xl text-sm font-medium capitalize border transition-colors ${
                        priorita === p
                          ? p === 'alta'  ? 'bg-red-500/20 border-red-500 text-red-300'
                          : p === 'media' ? 'bg-yellow-500/20 border-yellow-500 text-yellow-300'
                          :                 'bg-sky-500/20 border-sky-500 text-sky-300'
                          : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                      }`}>{p}</button>
                  ))}
                </div>
              </div>
              {progetti.length > 0 && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Progetto (opz.)</label>
                  <select value={projectIdTask} onChange={e => setProjectIdTask(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-violet-500 transition-colors">
                    <option value="">— nessuno —</option>
                    {progetti.map(p => <option key={p.id} value={p.id}>{p.titolo}</option>)}
                  </select>
                </div>
              )}
            </>
          )}

          <div className="flex gap-3 pt-1">
            <button onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-gray-700 text-gray-400 hover:text-white text-sm transition-colors">
              Annulla
            </button>
            <button onClick={handleSave}
              disabled={(tab === 'evento' ? !titolo.trim() : !tTask.trim()) || saving}
              className="flex-1 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white text-sm font-medium transition-colors">
              {saving ? 'Salvo...' : 'Aggiungi'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ─── Modal modifica evento ─── */
const EditEventModal = ({ evento, progetti, teamMembers, myId, onClose, onSave }) => {
  const existing = evento.calendar_partecipanti?.map(p => p.user_id) || []
  const [titolo, setTitolo]       = useState(evento.titolo || '')
  const [oraInizio, setOraInizio] = useState(evento.ora_inizio ? evento.ora_inizio.slice(0, 5) : '')
  const [oraFine, setOraFine]     = useState(evento.ora_fine ? evento.ora_fine.slice(0, 5) : '')
  const [descrizione, setDesc]    = useState(evento.descrizione || '')
  const [projectId, setProjectId] = useState(evento.project_id || '')
  const [partecipanti, setPartecipanti] = useState(existing)
  const [saving, setSaving]       = useState(false)

  const togglePartecipante = (id) =>
    setPartecipanti(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])

  const handleSave = async () => {
    if (!titolo.trim()) return
    setSaving(true)
    await onSave(evento.id, { titolo: titolo.trim(), oraInizio, oraFine, descrizione, projectId, partecipanti })
    setSaving(false)
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-5 pb-4">
          <div>
            <p className="text-sm font-semibold text-white">Modifica evento</p>
            <p className="text-xs text-gray-500 mt-0.5">{labelData(evento.data)}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors"><X size={18} /></button>
        </div>

        <div className="px-5 pb-5 space-y-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Titolo</label>
            <input autoFocus value={titolo} onChange={e => setTitolo(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSave()}
              placeholder="Nome dell'evento..."
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-violet-500 transition-colors" />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1.5">Dalle</label>
              <input type="time" value={oraInizio} onChange={e => setOraInizio(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500 transition-colors" />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1.5">Alle</label>
              <input type="time" value={oraFine} onChange={e => setOraFine(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500 transition-colors" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Descrizione (opz.)</label>
            <textarea value={descrizione} onChange={e => setDesc(e.target.value)} rows={2}
              placeholder="Note sull'evento..."
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-violet-500 resize-none transition-colors" />
          </div>
          {progetti.length > 0 && (
            <div>
              <label className="block text-xs text-gray-500 mb-1.5">Progetto (opz.)</label>
              <select value={projectId} onChange={e => setProjectId(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-violet-500 transition-colors">
                <option value="">— nessuno —</option>
                {progetti.map(p => <option key={p.id} value={p.id}>{p.titolo}</option>)}
              </select>
            </div>
          )}
          {teamMembers.length > 0 && (
            <div>
              <label className="block text-xs text-gray-500 mb-2">Partecipanti</label>
              <div className="flex flex-wrap gap-2">
                {teamMembers.filter(m => m.id !== myId).map(m => {
                  const sel = partecipanti.includes(m.id)
                  const name = m.nickname || m.full_name || '?'
                  return (
                    <button key={m.id} onClick={() => togglePartecipante(m.id)} title={name}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-medium border transition-all ${
                        sel
                          ? 'bg-violet-600/20 border-violet-500 text-violet-300'
                          : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500'
                      }`}>
                      <AvatarMini profile={m} size={4} />
                      {name}
                    </button>
                  )
                })}
              </div>
              {partecipanti.length > 0 && (
                <p className="text-xs text-violet-400 mt-1.5">{partecipanti.length} selezionati</p>
              )}
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-gray-700 text-gray-400 hover:text-white text-sm transition-colors">
              Annulla
            </button>
            <button onClick={handleSave} disabled={!titolo.trim() || saving}
              className="flex-1 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white text-sm font-medium transition-colors">
              {saving ? 'Salvo...' : 'Salva'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ─── Riga evento nel pannello / agenda ─── */
const EventRow = ({ ev, onEdit, onDelete, navigate, dotColor }) => {
  const [hover, setHover] = useState(false)
  const isClickable = ev.tipo !== 'evento'

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => isClickable && (ev.tipo === 'task' ? navigate('/tasks') : navigate('/progetti'))}
      className={`flex items-start gap-3 px-3 py-2.5 rounded-xl transition-colors ${
        isClickable ? 'hover:bg-gray-800/60 cursor-pointer' : 'bg-gray-800/30'
      } mb-1 group`}>
      <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${dotColor(ev)}`} />
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-medium truncate ${ev.status === 'done' ? 'line-through text-gray-500' : 'text-white'}`}>
          {ev.titolo}
        </p>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          {ev.tipo === 'evento' && (ev.ora_inizio || ev.ora_fine) && (
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Clock size={10} />
              {fmtTime(ev.ora_inizio)}{ev.ora_fine ? ` – ${fmtTime(ev.ora_fine)}` : ''}
            </span>
          )}
          <p className="text-xs text-gray-500">
            {ev.tipo === 'progetto' ? 'Scadenza progetto'
              : ev.tipo === 'evento' ? 'Evento'
              : `Task · ${ev.priorita}`}
          </p>
        </div>
        {ev.tipo === 'evento' && ev.calendar_partecipanti?.length > 0 && (
          <div className="flex items-center gap-1 mt-1.5">
            <Users size={10} className="text-gray-500" />
            <div className="flex -space-x-1">
              {ev.calendar_partecipanti.slice(0, 5).map((p, j) => (
                <div key={j} title={p.profiles?.nickname || p.profiles?.full_name}>
                  <AvatarMini profile={p.profiles} size={4} />
                </div>
              ))}
              {ev.calendar_partecipanti.length > 5 && (
                <div className="w-4 h-4 rounded-full bg-gray-700 flex items-center justify-center text-[8px] text-gray-400">
                  +{ev.calendar_partecipanti.length - 5}
                </div>
              )}
            </div>
            <span className="text-xs text-gray-500 ml-0.5">{ev.calendar_partecipanti.length}</span>
          </div>
        )}
        {ev.tipo === 'evento' && ev.descrizione && (
          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{ev.descrizione}</p>
        )}
      </div>
      {/* Azioni edit/delete solo per eventi */}
      {ev.tipo === 'evento' && (
        <div className={`flex gap-1 flex-shrink-0 transition-opacity ${hover ? 'opacity-100' : 'opacity-0'}`}>
          <button
            onClick={e => { e.stopPropagation(); onEdit(ev) }}
            className="p-1.5 rounded-lg text-gray-500 hover:text-violet-400 hover:bg-gray-700 transition-colors"
            title="Modifica">
            <Pencil size={13} />
          </button>
          <button
            onClick={e => { e.stopPropagation(); onDelete(ev.id) }}
            className="p-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-gray-700 transition-colors"
            title="Elimina">
            <Trash2 size={13} />
          </button>
        </div>
      )}
    </div>
  )
}

/* ─── Main ─── */
const Calendario = () => {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const myId = profile?.id

  const now = new Date()
  const [anno, setAnno]       = useState(now.getFullYear())
  const [mese, setMese]       = useState(now.getMonth())
  const [tasks, setTasks]     = useState([])
  const [progettiMese, setProgettiMese] = useState([])
  const [eventi, setEventi]   = useState([])
  const [tuttiProgetti, setTuttiProgetti] = useState([])
  const [teamMembers, setTeamMembers]     = useState([])
  const [selectedDay, setSelectedDay]     = useState(null)
  const [loading, setLoading] = useState(true)
  const [newEventDate, setNewEventDate]   = useState(null)
  const [editingEvento, setEditingEvento] = useState(null)
  const [view, setView]       = useState('grid') // 'grid' | 'agenda'

  const fetchDati = useCallback(async () => {
    const inizio = isoDate(startOfMonth(anno, mese))
    const fine   = isoDate(endOfMonth(anno, mese))

    const [{ data: t }, { data: pr }, { data: ev }] = await Promise.all([
      supabase.from('tasks')
        .select('id, titolo, scadenza, priorita, status, project_id')
        .not('scadenza', 'is', null)
        .gte('scadenza', inizio).lte('scadenza', fine)
        .order('scadenza'),
      supabase.from('progetti')
        .select('id, titolo, colore, scadenza')
        .not('scadenza', 'is', null)
        .gte('scadenza', inizio).lte('scadenza', fine),
      supabase.from('calendar_eventi')
        .select('*, calendar_partecipanti(user_id, profiles!user_id(id, full_name, nickname, avatar_url))')
        .gte('data', inizio).lte('data', fine)
        .order('data'),
    ])
    setTasks(t || [])
    setProgettiMese(pr || [])
    setEventi(ev || [])
    setLoading(false)
  }, [anno, mese])

  useEffect(() => {
    supabase.from('progetti').select('id, titolo, colore')
      .in('status', ['attivo', 'in_pausa']).order('titolo')
      .then(({ data }) => setTuttiProgetti(data || []))
    supabase.from('profiles').select('id, full_name, nickname, avatar_url')
      .neq('role', 'client').order('full_name')
      .then(({ data }) => setTeamMembers(data || []))
  }, [])

  useEffect(() => {
    setLoading(true)
    fetchDati()
    const ch = supabase.channel('calendario-rt')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks' }, fetchDati)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'progetti' }, fetchDati)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'calendar_eventi' }, fetchDati)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'calendar_partecipanti' }, fetchDati)
      .subscribe()
    return () => supabase.removeChannel(ch)
  }, [fetchDati])

  const prevMese = () => {
    if (mese === 0) { setAnno(a => a - 1); setMese(11) } else setMese(m => m - 1)
    setSelectedDay(null)
  }
  const nextMese = () => {
    if (mese === 11) { setAnno(a => a + 1); setMese(0) } else setMese(m => m + 1)
    setSelectedDay(null)
  }

  /* ─── Save nuovo evento ─── */
  const handleSaveEvento = async ({ titolo, oraInizio, oraFine, descrizione, projectId, partecipanti }) => {
    if (!myId) return
    const { data: ev, error } = await supabase.from('calendar_eventi').insert({
      titolo,
      data: newEventDate,
      ora_inizio: oraInizio || null,
      ora_fine: oraFine || null,
      descrizione: descrizione || null,
      project_id: projectId || null,
      created_by: myId,
    }).select().single()
    if (error) { console.error('[Calendario] insert evento:', error.message); return }
    if (!ev) return
    if (partecipanti.length > 0) {
      await supabase.from('calendar_partecipanti').insert(
        partecipanti.map(uid => ({ evento_id: ev.id, user_id: uid, added_by: myId }))
      )
    }
    setNewEventDate(null)
    fetchDati()
  }

  /* ─── Update evento ─── */
  const handleUpdateEvento = async (id, { titolo, oraInizio, oraFine, descrizione, projectId, partecipanti }) => {
    await supabase.from('calendar_eventi').update({
      titolo,
      ora_inizio: oraInizio || null,
      ora_fine: oraFine || null,
      descrizione: descrizione || null,
      project_id: projectId || null,
    }).eq('id', id)
    await supabase.from('calendar_partecipanti').delete().eq('evento_id', id)
    if (partecipanti.length > 0) {
      await supabase.from('calendar_partecipanti').insert(
        partecipanti.map(uid => ({ evento_id: id, user_id: uid, added_by: myId }))
      )
    }
    setEditingEvento(null)
    fetchDati()
  }

  /* ─── Delete evento ─── */
  const handleDeleteEvento = async (id) => {
    if (!window.confirm('Eliminare questo evento?')) return
    await supabase.from('calendar_partecipanti').delete().eq('evento_id', id)
    await supabase.from('calendar_eventi').delete().eq('id', id)
    fetchDati()
  }

  const handleSaveTask = async ({ titolo, priorita, projectId }) => {
    if (!myId) return
    await supabase.from('tasks').insert({
      titolo, priorita,
      project_id: projectId || null,
      scadenza: newEventDate,
      status: 'todo',
      creato_da: myId,
    })
    setNewEventDate(null)
    fetchDati()
  }

  /* ─── Griglia ─── */
  const primo  = startOfMonth(anno, mese)
  const ultimo = endOfMonth(anno, mese)
  const offsetInizio = dowLun(primo)
  const totaleCelle  = Math.ceil((offsetInizio + ultimo.getDate()) / 7) * 7
  const celle = Array.from({ length: totaleCelle }, (_, i) => {
    const g = i - offsetInizio + 1
    return g < 1 || g > ultimo.getDate() ? null : g
  })

  /* ─── Indice per giorno ─── */
  const eventiPerGiorno = {}
  tasks.forEach(t => {
    if (!eventiPerGiorno[t.scadenza]) eventiPerGiorno[t.scadenza] = []
    eventiPerGiorno[t.scadenza].push({ ...t, tipo: 'task' })
  })
  progettiMese.forEach(p => {
    if (!eventiPerGiorno[p.scadenza]) eventiPerGiorno[p.scadenza] = []
    eventiPerGiorno[p.scadenza].push({ ...p, tipo: 'progetto' })
  })
  eventi.forEach(ev => {
    if (!eventiPerGiorno[ev.data]) eventiPerGiorno[ev.data] = []
    eventiPerGiorno[ev.data].push({ ...ev, tipo: 'evento' })
  })

  const todayStr    = isoDate(now)
  const selectedStr = selectedDay
    ? `${anno}-${String(mese + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`
    : null
  const eventiSelected = selectedStr ? (eventiPerGiorno[selectedStr] || []) : []

  const dotColor = (ev) => {
    if (ev.tipo === 'progetto') return 'bg-violet-400'
    if (ev.tipo === 'evento')   return 'bg-emerald-400'
    return PRIORITA_COLOR[ev.priorita] || 'bg-gray-400'
  }

  /* ─── Agenda: tutti gli item del mese ordinati per data ─── */
  const agendaItems = Object.entries(eventiPerGiorno)
    .sort(([a], [b]) => a.localeCompare(b))

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <h1 className="text-2xl font-bold text-white">Calendario</h1>
        <div className="flex items-center gap-2">
          {/* Toggle griglia / agenda */}
          <div className="flex bg-gray-800 rounded-xl p-1 gap-0.5">
            <button
              onClick={() => setView('grid')}
              className={`p-2 rounded-lg transition-colors ${view === 'grid' ? 'bg-gray-700 text-white' : 'text-gray-500 hover:text-white'}`}
              title="Calendario">
              <CalendarDays size={16} />
            </button>
            <button
              onClick={() => setView('agenda')}
              className={`p-2 rounded-lg transition-colors ${view === 'agenda' ? 'bg-gray-700 text-white' : 'text-gray-500 hover:text-white'}`}
              title="Agenda">
              <List size={16} />
            </button>
          </div>
          <button onClick={prevMese} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors">
            <ChevronLeft size={18} />
          </button>
          <span className="text-white font-semibold min-w-36 text-center">{MESI[mese]} {anno}</span>
          <button onClick={nextMese} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* ── VISTA AGENDA ── */}
      {view === 'agenda' && (
        <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-white">Agenda — {MESI[mese]} {anno}</h2>
            <span className="text-xs text-gray-500">
              {tasks.length + eventi.length + progettiMese.length} elementi
            </span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : agendaItems.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-500 text-sm">Nessun evento in {MESI[mese]}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-800/60">
              {agendaItems.map(([dateStr, items]) => {
                const [y, m, d] = dateStr.split('-')
                const dayNum = parseInt(d)
                const isToday = dateStr === todayStr
                const dayDate = new Date(parseInt(y), parseInt(m) - 1, dayNum)
                const dowLabel = GIORNI[dowLun(dayDate)]
                return (
                  <div key={dateStr} className="px-4 py-3">
                    {/* Data header */}
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`flex flex-col items-center justify-center w-10 h-10 rounded-xl flex-shrink-0 ${
                        isToday ? 'bg-violet-600' : 'bg-gray-800'
                      }`}>
                        <span className={`text-xs font-medium ${isToday ? 'text-violet-200' : 'text-gray-500'}`}>{dowLabel}</span>
                        <span className={`text-base font-bold leading-tight ${isToday ? 'text-white' : 'text-gray-300'}`}>{dayNum}</span>
                      </div>
                      <div className="flex-1 h-px bg-gray-800" />
                      <button
                        onClick={() => { setNewEventDate(dateStr); setView('grid'); setTimeout(() => setSelectedDay(dayNum), 50) }}
                        className="text-xs text-gray-600 hover:text-violet-400 flex items-center gap-1 transition-colors"
                        title="Aggiungi">
                        <Plus size={12} /> aggiungi
                      </button>
                    </div>
                    {/* Items */}
                    <div className="pl-12 space-y-0.5">
                      {items.map((ev, i) => (
                        <EventRow
                          key={i}
                          ev={ev}
                          onEdit={setEditingEvento}
                          onDelete={handleDeleteEvento}
                          navigate={navigate}
                          dotColor={dotColor}
                        />
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ── VISTA GRIGLIA ── */}
      {view === 'grid' && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

          {/* Griglia */}
          <div className="xl:col-span-2 bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
            <div className="grid grid-cols-7 border-b border-gray-800">
              {GIORNI.map(g => (
                <div key={g} className="py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{g}</div>
              ))}
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-7">
                {celle.map((giorno, idx) => {
                  if (!giorno) return <div key={idx} className="min-h-16 sm:min-h-20 border-b border-r border-gray-800/50" />
                  const dateStr    = `${anno}-${String(mese + 1).padStart(2, '0')}-${String(giorno).padStart(2, '0')}`
                  const isToday    = dateStr === todayStr
                  const isSelected = dateStr === selectedStr
                  const evs        = eventiPerGiorno[dateStr] || []

                  return (
                    <div key={idx} onClick={() => setSelectedDay(giorno === selectedDay ? null : giorno)}
                      className={`min-h-16 sm:min-h-20 border-b border-r border-gray-800/50 p-1.5 sm:p-2 cursor-pointer transition-colors group relative ${
                        isSelected ? 'bg-violet-600/10' : 'hover:bg-gray-800/40'
                      }`}>
                      <div className="flex items-center justify-between">
                        <span className={`text-xs sm:text-sm font-medium inline-flex w-6 h-6 sm:w-7 sm:h-7 items-center justify-center rounded-full ${
                          isToday ? 'bg-violet-600 text-white' : isSelected ? 'text-violet-400' : 'text-gray-400'
                        }`}>{giorno}</span>
                        <button
                          onClick={e => { e.stopPropagation(); setNewEventDate(dateStr) }}
                          className="opacity-0 group-hover:opacity-100 w-5 h-5 rounded-md bg-gray-700 hover:bg-violet-600 text-gray-400 hover:text-white flex items-center justify-center transition-all"
                          title="Aggiungi">
                          <Plus size={11} />
                        </button>
                      </div>
                      {evs.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {evs.slice(0, 3).map((ev, i) => (
                            <span key={i} className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColor(ev)}`} />
                          ))}
                          {evs.length > 3 && <span className="text-xs text-gray-600">+{evs.length - 3}</span>}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            {/* Legenda */}
            <div className="px-4 py-3 border-t border-gray-800 flex items-center gap-3 flex-wrap">
              <span className="flex items-center gap-1.5 text-xs text-gray-500"><span className="w-2 h-2 rounded-full bg-emerald-400" /> Evento</span>
              <span className="flex items-center gap-1.5 text-xs text-gray-500"><span className="w-2 h-2 rounded-full bg-violet-400" /> Progetto</span>
              <span className="flex items-center gap-1.5 text-xs text-gray-500"><span className="w-2 h-2 rounded-full bg-red-400" /> Task alta</span>
              <span className="flex items-center gap-1.5 text-xs text-gray-500"><span className="w-2 h-2 rounded-full bg-yellow-400" /> Task media</span>
              <span className="flex items-center gap-1.5 text-xs text-gray-500"><span className="w-2 h-2 rounded-full bg-sky-400" /> Task bassa</span>
            </div>
          </div>

          {/* Pannello laterale */}
          <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden flex flex-col">
            <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-white">
                {selectedDay ? `${selectedDay} ${MESI[mese]}` : 'Seleziona un giorno'}
              </h2>
              {selectedDay && (
                <button onClick={() => setNewEventDate(selectedStr)}
                  className="flex items-center gap-1 text-xs text-violet-400 hover:text-violet-300 transition-colors">
                  <Plus size={13} /> Aggiungi
                </button>
              )}
            </div>

            <div className="p-3 flex-1 overflow-y-auto">
              {!selectedDay && (
                <p className="text-gray-600 text-sm text-center py-8">Clicca su un giorno per vedere le scadenze</p>
              )}
              {selectedDay && eventiSelected.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-600 text-sm">Nessun evento in questo giorno</p>
                  <button onClick={() => setNewEventDate(selectedStr)}
                    className="mt-3 flex items-center gap-1.5 text-xs text-violet-400 hover:text-violet-300 transition-colors mx-auto">
                    <Plus size={13} /> Aggiungi
                  </button>
                </div>
              )}

              {eventiSelected.map((ev, i) => (
                <EventRow
                  key={i}
                  ev={ev}
                  onEdit={setEditingEvento}
                  onDelete={handleDeleteEvento}
                  navigate={navigate}
                  dotColor={dotColor}
                />
              ))}
            </div>

            {/* Riepilogo mese */}
            <div className="px-5 py-4 border-t border-gray-800">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Questo mese</p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Eventi</span>
                  <span className="text-emerald-400 font-medium">{eventi.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Task con scadenza</span>
                  <span className="text-white font-medium">{tasks.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Scadenze progetto</span>
                  <span className="text-white font-medium">{progettiMese.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">
                    <Circle size={8} className="inline mr-1 text-red-400 fill-red-400" />
                    Alta priorità
                  </span>
                  <span className="text-red-400 font-medium">{tasks.filter(t => t.priorita === 'alta').length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modali */}
      {newEventDate && (
        <NewEventModal
          dateStr={newEventDate}
          progetti={tuttiProgetti}
          teamMembers={teamMembers}
          myId={myId}
          onClose={() => setNewEventDate(null)}
          onSaveEvento={handleSaveEvento}
          onSaveTask={handleSaveTask}
        />
      )}
      {editingEvento && (
        <EditEventModal
          evento={editingEvento}
          progetti={tuttiProgetti}
          teamMembers={teamMembers}
          myId={myId}
          onClose={() => setEditingEvento(null)}
          onSave={handleUpdateEvento}
        />
      )}
    </div>
  )
}

export default Calendario
